import { motion } from "framer-motion";
import { Flame, Star, Trophy, Hash } from "lucide-react";
import { badgeIcon } from "@/lib/badges";
import { numIN } from "@/lib/format";

export default function GamificationStrip({ summary }) {
  if (!summary) return null;
  const cards = [
    { label: "Points", value: numIN(summary.points), icon: Star, tint: "text-emerald-600", testid: "gami-points" },
    {
      label: "Current streak",
      value: `${summary.streak} ${summary.streak === 1 ? "day" : "days"}`,
      icon: Flame,
      tint: summary.streak > 0 ? "text-red-500" : "text-muted-foreground",
      pulse: summary.streak >= 7,
      testid: "gami-streak",
    },
    { label: "Your rank", value: summary.rank ? `#${summary.rank}` : "—", sub: `of ${numIN(summary.total_coaches || 0)}`, icon: Hash, tint: "text-amber-600", testid: "gami-rank" },
    { label: "Badges earned", value: `${summary.badges_earned}/${summary.badges?.length || 0}`, icon: Trophy, tint: "text-amber-600", testid: "gami-badges" },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid={c.testid}>
            <c.icon className={`h-5 w-5 mb-3 ${c.tint} ${c.pulse ? "animate-pulse" : ""}`} />
            <div className="font-mono-num text-3xl font-bold">{c.value}</div>
            <div className="text-sm text-muted-foreground mt-1">{c.label}{c.sub ? ` · ${c.sub}` : ""}</div>
          </motion.div>
        ))}
      </div>

      {(() => {
        const earned = (summary.badges || []).filter((b) => b.earned);
        return (
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Achievements</div>
            {earned.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {earned.map((b) => {
                  const Icon = badgeIcon(b.key);
                  return (
                    <div key={b.key} data-testid={`badge-${b.key}`}
                         className="flex items-center gap-2 rounded-full px-3.5 py-2 border bg-amber-50 border-amber-200 text-amber-800 transition-all">
                      <Icon className="h-4 w-4 text-amber-500" />
                      <span className="text-sm font-medium">{b.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="no-achievements">
                No achievements yet — log your activity and close clients to unlock badges.
              </p>
            )}
          </div>
        );
      })()}
    </div>
  );
}
